import { bootstrapCameraKit, createMediaStreamSource, Transform2D } from '@snap/camera-kit';
import {createContext, useEffect, useRef, useState} from 'react';
import keys from '../keys.json';
import Camera from "../libs/camera";
import {ORIENTATION} from "../constants";

const HEALTH_CHECK_INTERVAL = 5000;

// Production token: the staging one renders Snap's "Staging" watermark
const apiToken = keys.PRODUCTION_API_KEY;
const lensGroupId = keys.LENS_GROUP_ID;

export const CameraKitContext = createContext(null);

export const CameraKit = ({ canvasRef, showDebug, orientation, children, onChangeCameraFacing }) => {
    const camera = useRef(null);
    const [isStarting, setIsStarting] = useState(true);
    const [started, setStarted] = useState(false);
    const [lenses, setLenses] = useState([]);
    const [isApplyingLens, setIsApplyingLens] = useState(false);
    const [lensIndex, setLensIndex] = useState(0);
    const [cameraFacing, setCameraFacing] = useState(Camera.BACK_CAMERA);

    // Must be called from a user gesture: iOS only shows the motion/gyroscope
    // permission prompt inside a tap's user-activation window.
    function start() {
        if (camera.current) return showDebug('Camera is already created');
        setStarted(true);
        camera.current = new Camera(showDebug);
        // Another app stealing the camera also ends the track while we are in
        // the foreground; recover from that the same way.
        camera.current.onStreamLost = () => {
            if (document.visibilityState === 'visible') {
                camera.current.recover().catch(err => showDebug(err.message));
            }
        };
        const config = {
            apiToken: apiToken,
            lensGroupId: lensGroupId,
            canvasRef: canvasRef.current,
            cameraFacing: cameraFacing,
            lensIndex: lensIndex,
        };
        camera.current.initialize(config)
            .then(cam => setLenses(cam.lenses))
            .catch(err => showDebug(err.message))
            .finally(() => setIsStarting(false));
    }

    function changeLens(lensIndex) {
        setLensIndex(lensIndex);
    }

    function toggleCamera() {
        const facing = cameraFacing === Camera.FRONT_CAMERA
            ? Camera.BACK_CAMERA
            : Camera.FRONT_CAMERA;
        setCameraFacing(facing);
    }

    useEffect(() => {
        if (!camera.current) return;
        setIsApplyingLens(true);
        camera.current.applyLens(lensIndex)
            .catch(err => {
                showDebug(err.message);
                console.error(err);
            })
            .finally(() => setIsApplyingLens(false));
    }, [ lensIndex ]);

    useEffect(() => {
        if (!camera.current) return;
        camera.current.changeCameraFacing(cameraFacing)
            .then(() => showDebug(`Camera has been changed to ${cameraFacing}`))
            .then(() => onChangeCameraFacing(cameraFacing))
            .catch(err => {
                showDebug(err.message);
                console.error(err);
            });
    }, [ cameraFacing ]);

    useEffect(() => {
        if (!camera.current) return;
        if (orientation === ORIENTATION.PORTRAIT) {
            camera.current.resume().catch(showDebug);
        } else if (orientation === ORIENTATION.LANDSCAPE) {
            camera.current.pause().catch(showDebug);
        }
    }, [ orientation ]);

    // Periodic safety net for freezes that happen without any visibility
    // change. The check is passive (it samples Camera Kit's own frame counter)
    // and only acts after two consecutive stalled samples.
    useEffect(() => {
        const timer = setInterval(() => {
            if (!camera.current) return;
            camera.current.runHealthCheck().catch(err => console.error(err));
        }, HEALTH_CHECK_INTERVAL);
        return () => clearInterval(timer);
    }, []);

    // Backgrounding the browser suspends the camera; iOS usually ends the video
    // track outright, which leaves the canvas frozen on the last frame once the
    // user comes back. Rebuild the feed on every return to the foreground.
    useEffect(() => {
        let retryTimer = null;

        const clearRetry = () => {
            if (retryTimer) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
            document.removeEventListener('touchend', recoverOnGesture);
            document.removeEventListener('click', recoverOnGesture);
        };

        // Last resort: some iOS builds refuse getUserMedia outside a user
        // gesture, so let the next tap anywhere retry the recovery.
        function recoverOnGesture() {
            clearRetry();
            attemptRecovery();
        }

        function attemptRecovery(retriesLeft = 2) {
            const cam = camera.current;
            if (!cam || !cam.session || document.visibilityState !== 'visible') return;

            cam.recover().then(ok => {
                if (ok) return clearRetry();
                if (retriesLeft > 0) {
                    retryTimer = setTimeout(() => attemptRecovery(retriesLeft - 1), 600);
                } else {
                    showDebug('Camera needs a tap to resume');
                    document.addEventListener('touchend', recoverOnGesture, { once: true });
                    document.addEventListener('click', recoverOnGesture, { once: true });
                }
            });
        }

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return clearRetry();
            // iOS needs a beat after resuming before it will hand back a stream.
            retryTimer = setTimeout(() => attemptRecovery(), 250);
        };

        // bfcache restores (back navigation, tab switch on some browsers) do not
        // always fire visibilitychange.
        const onPageShow = (event) => {
            if (event.persisted) attemptRecovery();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pageshow', onPageShow);
        window.addEventListener('focus', onVisibilityChange);

        return () => {
            clearRetry();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pageshow', onPageShow);
            window.removeEventListener('focus', onVisibilityChange);
        };
    }, []);

    // Reads the lens' clean 'capture' output rather than the live canvas, so the
    // lens' own on-screen UI is left out of photos and videos.
    function capturePhoto(contentType, drawOverlay) {
        if (!camera.current) return Promise.reject(new Error('Camera is not ready'));
        return camera.current.capturePhoto(contentType, drawOverlay);
    }

    function createCaptureStream(fps) {
        if (!camera.current) return Promise.resolve(null);
        return camera.current.createCaptureStream(fps);
    }

    function stopCaptureRendering() {
        if (!camera.current) return Promise.resolve();
        return camera.current.stopCaptureRendering();
    }

    return <CameraKitContext.Provider value={{
        lenses, lensIndex, showDebug, isApplyingLens, isStarting, started, start, changeLens, toggleCamera,
        capturePhoto, createCaptureStream, stopCaptureRendering,
    }}>
        {children}
    </CameraKitContext.Provider>;
}
